import React, { FC, useCallback, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { PageActionButton } from 'uiComponents/buttons/pageAddButton'
import { DatePicker } from 'uiComponents/popups/datePickerInput'
import OrdersSectionTabs from 'orders/sectionTabs'
import { FormItem } from 'uiComponents/form/formElements'
import { Button } from '@mui/material'
import CapacityReportTimeline from './components/CapacityReportTimeline'
import CapacityReportFilters from './components/CapacityReportFilters'
import { useGetAccountLocationsQuery } from 'orders/reduxQueries'
import { useAppSelector } from 'store/hooks'
import { getActiveAccount } from 'preferences/selectors'
import { useTranslation } from 'translations'
import './capacityReportPage.scss'

const CapacityReport: FC = () => {
    const { t } = useTranslation()
    const accountSlug = useAppSelector(getActiveAccount)
    if (!accountSlug) throw new Error('No account selected')
    const { data: locationData } = useGetAccountLocationsQuery({ accountSlug })
    const [selectedDate, setSelectedDate] = useState<Date>(new Date())
    const handleDateChange = useCallback((date: Date) => {
        setSelectedDate(date)
    }, [])

    const stepDate = useCallback(
        (step: 'increment' | 'decrement') => () => {
            const date = new Date(selectedDate)
            const newDate = new Date(date)

            if (step === 'increment') newDate.setDate(date.getDate() + 1)
            else newDate.setDate(date.getDate() - 1)

            handleDateChange(newDate)
        },
        [selectedDate, handleDateChange],
    )

    const hasMoreThanOneLocation = locationData?.length && locationData?.length > 1
    const shouldShowFilters = hasMoreThanOneLocation

    return (
        <>
            <OrdersSectionTabs />
            <div className="capacity-controls">
                <div className="capacity-controls__left">{shouldShowFilters && <CapacityReportFilters />}</div>
                <div className="capacity-controls__right">
                    <PageActionButton onClick={() => handleDateChange(new Date())}>
                        {t('orders.capacityReport.today')}
                    </PageActionButton>
                    <FormItem className="date-picker" htmlFor="validFrom">
                        <DatePicker
                            id="selectedDate"
                            date={selectedDate ? new Date(selectedDate) : null}
                            onChange={handleDateChange}
                        />
                    </FormItem>
                    <Button
                        className="day-step-button"
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={stepDate('decrement')}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <Button
                        className="day-step-button"
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={stepDate('increment')}
                    >
                        <ChevronRight size={16} />
                    </Button>
                </div>
            </div>
            <CapacityReportTimeline selectedDate={selectedDate} />
        </>
    )
}

export default CapacityReport
